// Affiliate tours — Viator, and on dubai also Click&Boat's boat trips
// (source 'click_and_boat'). Separate object from DbEvent (supabase.ts):
// evergreen experiences with rating / duration / from-price and an
// affiliate-tagged booking_url, no fixed date. Read via the `tours_by_city`
// RPC (see hooks/use-tours.ts).

import { SITE } from '@site';

import type { SiteConfig } from './site-config';
import type { LocaleCopy } from './supabase';

// The `tours.translations` JSONB column — same shape as events.translations,
// produced by the translate-tours Edge Function. Per-locale keys are optional
// so an un-translated / partially-failed tour still typechecks and the
// localizedTour* selectors (./i18n/tour-content.ts) can fall back to the
// raw Viator text. The leading-underscore keys are generator bookkeeping
// (source hash, model, timestamp) and aren't read by the app.
export type TourTranslations = {
  _src_hash?: string;
  _model?: string;
  _ts?: string;
  en?: TourLocaleCopy;
  fr?: TourLocaleCopy;
  de?: TourLocaleCopy;
  es?: TourLocaleCopy;
  ru?: TourLocaleCopy;
};

/**
 * One locale's tour copy. `facts`: the details lists translated into a
 * non-English locale (translate-tours' facts.ts) — a list missing from it
 * (count mismatch, not translated yet) is shown in English. Read it through
 * localizedTourFacts().
 */
export type TourLocaleCopy = LocaleCopy & { facts?: Partial<Record<TourFactList, string[]>> };

/** The tours.details lists the tour page shows. */
export type TourFactList = 'included' | 'excluded' | 'knowBefore';

export type Tour = {
  id: string;
  source: string;
  source_id: string;
  title: string;
  description: string | null;
  city_id: string | null;
  rating: number | null;
  review_count: number | null;
  duration: string | null;
  from_price: number | null;
  currency: string | null;
  image_url: string | null;
  booking_url: string | null;
  /**
   * When sync-viator found the tour INACTIVE (or 404) on Viator; null = on
   * sale. A delisted tour is out of every list but keeps its page — "no longer
   * available", noindex — until it's purged 30 days later
   * (supabase/migrations/20261008140000_tours_soft_delist.sql).
   */
  delisted_at: string | null;
  /**
   * Classifier output (e.g. `{wine}`), set by a DB trigger from
   * `tour_themes(title, raw)` — see 20260923120000_tour_themes.sql. Drives the
   * themed "more like this" block on a tour page. Optional: list seeds trim it.
   */
  themes?: string[] | null;
  translations?: TourTranslations | null;
  /**
   * Viator's PRIVATE_TOUR flag, which lives only in `raw`. Baked into the
   * /[city]/tours seed (which drops `raw`) for that page's private/group
   * filter; absent elsewhere. Read it through tourIsPrivate().
   */
  is_private?: boolean;
  /**
   * Viator product facts, written by sync-viator on sites with the
   * viator_product_details feature (20261024120000_tour_details.sql); selected
   * only where SITE.tourDetails is on (tourDetailColumns()).
   */
  details?: TourDetails | null;
};

/** tours.details — the shape sync-viator's buildDetails() writes. Lists are the supplier's English text. */
export type TourDetails = {
  /** The English fact sheet translate-tours writes the copy from; not shown. */
  text?: string;
  itinerary?: string[];
  included?: string[];
  excluded?: string[];
  knowBefore?: string[];
  /** Viator pickupOptionType: PICKUP_EVERYONE | PICKUP_AND_MEET_AT_START_POINT | MEET_EVERYONE_AT_START_POINT. */
  pickup?: string | null;
  privateTour?: boolean | null;
  maxTravelers?: number | null;
  /** ISO 639 codes, e.g. ['en', 'ar']. */
  guideLanguages?: string[];
};

/**
 * Whether Viator flags the tour PRIVATE_TOUR: the baked `is_private`, else
 * the `raw` payload a full row carries (an unseeded client fetch). Same rule
 * as tours_filtered's p_private (20261012120000_hub_filters.sql).
 */
export function tourIsPrivate(tour: Tour): boolean {
  if (tour.is_private != null) return tour.is_private;
  const flags = (tour as Tour & { raw?: { flags?: unknown } }).raw?.flags;
  return Array.isArray(flags) && flags.includes('PRIVATE_TOUR');
}

// Explicit column list matching Tour — excludes fetched_at/updated_at/raw,
// which change on every sync-viator run regardless of whether anything
// user-visible changed. See EVENT_COLUMNS in supabase.ts for why
// that matters for the static export.
//
// `translations` is safe to include here despite that churn concern: it's
// stable once generated (hash-gated — only rewritten when the source text
// changes), so it won't reintroduce daily HTML churn. The tours_by_city RPC
// returns `*`, so list/carousel reads already carry it; this list is what the
// detail fetch (use-tour.ts) and the static-export prefetch (tour-detail.ts)
// select, so it must name `translations` explicitly.
export const TOUR_COLUMNS =
  'id,source,source_id,title,description,city_id,rating,review_count,duration,from_price,currency,image_url,booking_url,delisted_at,themes,translations';

/**
 * What the tour page selects: TOUR_COLUMNS, plus `details` on a site with
 * SITE.tourDetails. Like translations, details only change when Viator's
 * product does (details_fetched_at, which moves on every refetch, stays out).
 */
export function tourDetailColumns(): string {
  return (SITE as SiteConfig).tourDetails ? `${TOUR_COLUMNS},details` : TOUR_COLUMNS;
}

/** Viator no longer sells the tour — see Tour.delisted_at. */
export function isTourDelisted(tour: Tour): boolean {
  return tour.delisted_at != null;
}

// sync-viator's pickImage() always stores the *largest* available variant —
// there's no smaller one on offer at ingest time — so a 240px card and a
// full-bleed hero request the exact same file today.
const RESIZABLE_TRIPADVISOR_HOST = 'dynamic-media-cdn.tripadvisor.com';

// The older `media-cdn.tripadvisor.com` host has no `?w=&h=`, but the size in
// its `/attractions-splice-spp-720x480/` path segment can be swapped for one
// of these fixed variants (smallest first; some answer with a 301 to the
// dynamic host, which the browser follows). 720x480 is what the feed stores,
// so a request for more just keeps it.
const SPLICE_SIZE = /\/attractions-splice-spp-(\d+)x(\d+)\//;
const SPLICE_VARIANTS = [
  [360, 240],
  [480, 320],
  [674, 446],
] as const;

// Click&Boat trips (sync-click-and-boat-activities) ship Cloudinary fetch URLs
// with a responsive width (`w_auto:100:1000`), which without client hints comes
// back ~1000px wide; a fixed `w_…,h_…` in its place is honoured (480x320:
// ~18KB instead of ~55KB, checked 2026-10-25).
const CLOUDINARY_HOST = 'res.cloudinary.com';
const CLOUDINARY_AUTO_WIDTH = /\/c_fill,g_auto,w_auto(?::\d+)*\//;

/**
 * Requests a specific pixel size from TripAdvisor's CDNs (Viator) or
 * Cloudinary (Click&Boat).
 *
 * `dynamic-media-cdn.tripadvisor.com` honours `?w=&h=` exactly; the older
 * `media-cdn.tripadvisor.com` gets the smallest fixed `attractions-splice-spp`
 * variant at least `width` wide (a 720x480 card thumbnail was ~90KB, 480x320
 * is ~45KB — it fed Lighthouse's simulated mobile LCP on the home page). Any
 * other URL passes through unchanged.
 */
export function sizedTourImage(
  url: string | null | undefined,
  width: number,
  height: number,
): string | null {
  if (!url) return null;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }
  if (parsed.hostname === CLOUDINARY_HOST) {
    return url.replace(CLOUDINARY_AUTO_WIDTH, `/c_fill,g_auto,w_${width},h_${height}/`);
  }
  if (parsed.hostname === 'media-cdn.tripadvisor.com') {
    const stored = parsed.pathname.match(SPLICE_SIZE);
    const variant = SPLICE_VARIANTS.find(([w]) => w >= width);
    if (!stored || !variant || variant[0] >= Number(stored[1])) return url;
    return url.replace(SPLICE_SIZE, `/attractions-splice-spp-${variant[0]}x${variant[1]}/`);
  }
  if (parsed.hostname !== RESIZABLE_TRIPADVISOR_HOST) return url;
  parsed.searchParams.set('w', String(width));
  parsed.searchParams.set('h', String(height));
  return parsed.toString();
}
